import { ChevronDown, ChevronRight } from "lucide-react";
import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  ARTIFACT_PAGE_LIMIT,
  useArtifactPage,
  type Artifact,
  type ArtifactFacetValue,
  type ArtifactPage,
  type GroupDimension,
} from "@/api/artifacts";
import { ArtifactDownloadButton } from "@/components/artifacts/ArtifactDownloadButton";
import { ArtifactStatusBadge } from "@/components/artifacts/ArtifactStatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiErrorMessage, classifyApiError } from "@/lib/api-error-kind";
import { useDebouncedValue } from "@/lib/use-debounced-value";
import {
  FILTER_LABELS,
  FILTER_PARAMS,
  GROUP_BY_LABELS,
  GROUP_DIMENSIONS,
  filtersFromParams,
  groupLabel,
  groupLink,
  hasActiveFilters,
  isGroupBy,
  optionsFromFacet,
  runOptions,
  statusOptions,
  tileOptions,
  toArtifactQuery,
  type ArtifactFilters,
  type FilterOption,
  type GroupBy,
} from "@/routes/artifacts/artifact-filters";

/**
 * The artifacts tab: the catalog, filtered and counted BY THE SERVER
 * (flowstack#963).
 *
 * WHAT THE COUNTS ON THIS PAGE COUNT. All of them are catalog-wide under the
 * active filters. `GET /api/artifacts` applies each filter as a SQL predicate
 * before the limit and returns a real `total`, so "1 340 artifacts match" is a
 * fact about the catalog and a group heading that says 42 means 42 exist. The
 * page is a WINDOW onto that set — 200 rows at a time — and says which window
 * it is showing rather than warning that it might be incomplete.
 *
 * That is what replaced the standing truncation notice #965/#966 had to
 * render. The notice was correct at the time: filtering and grouping happened
 * in the browser over one page, that page was drawn from a capped run scan,
 * and nothing in the response could distinguish a complete answer from a
 * partial one. All three of those are now false, so the notice would be wrong
 * in the other direction — it would warn about a limitation that no longer
 * exists. What survives it is narrower and still true: the Run filter offers
 * the most recent runs rather than every run the tenant has ever had, and it
 * says so where that control lives.
 *
 * Filter, grouping and page state serializes to the URL (`?q=&type=&run=&tile=
 * &status=&group=&page=`), matching `RunListAgGrid`, so a filtered view
 * survives paste, bookmark and reload.
 */
export function ArtifactListPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = React.useMemo<ArtifactFilters>(
    () => filtersFromParams(searchParams),
    [searchParams]
  );
  const groupParam = searchParams.get("group") ?? "none";
  const groupBy: GroupBy = isGroupBy(groupParam) ? groupParam : "none";
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
  const offset = (page - 1) * ARTIFACT_PAGE_LIMIT;

  // The URL takes each keystroke so the address bar stays shareable; the
  // REQUEST waits for the typing to stop. Only `q` is debounced — the selects
  // change once per interaction, and delaying those would feel broken.
  const debouncedQ = useDebouncedValue(filters.q, 250);
  const query = React.useMemo(
    () =>
      toArtifactQuery(
        { ...filters, q: debouncedQ },
        { groupBy, offset, limit: ARTIFACT_PAGE_LIMIT }
      ),
    [filters, debouncedQ, groupBy, offset]
  );
  const { data, isLoading, isError, error } = useArtifactPage(query);

  const setParam = React.useCallback(
    (name: string, value: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (value) params.set(name, value);
          else params.delete(name);
          // Any change to what is being asked for invalidates which page of
          // the answer you were on: page 4 of an unfiltered catalog is not
          // page 4 of a filtered one, and landing on an empty page reads as
          // "no results" when it means "past the end".
          if (name !== "page") params.delete("page");
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const clearFilters = React.useCallback(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        for (const name of FILTER_PARAMS) params.delete(name);
        params.delete("page");
        return params;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  if (isLoading) {
    return (
      <div role="status" aria-live="polite" className="p-6 text-sm text-muted-foreground">
        Loading artifacts…
      </div>
    );
  }

  if (isError || !data) {
    return <ArtifactsError error={error} />;
  }

  const filtered = hasActiveFilters(filters);

  // Genuinely empty: the catalog holds nothing at all. Distinct from "nothing
  // matched", which is handled below — a user who filters into an empty set
  // must be able to tell which of the two they are looking at. With server
  // filtering the two are now distinguishable for certain rather than by
  // inference: an unfiltered total of 0 is the catalog's own count.
  if (data.total === 0 && !filtered) {
    return (
      <Card elevation="raised">
        <CardHeader>
          <CardTitle>No artifacts yet</CardTitle>
          <CardDescription>
            Artifacts land here as your tiles and flows produce them. Submit a Tile or Flow to
            populate the catalog.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Artifacts</h1>
        <p className="text-sm text-muted-foreground">
          <PageSummary page={data} filtered={filtered} /> Tenant-scoped catalog per anchor §5.2.
        </p>
      </div>

      <ArtifactToolbar
        page={data}
        filters={filters}
        groupBy={groupBy}
        onFilterChange={setParam}
        onClear={clearFilters}
      />

      {data.artifacts.length === 0 ? (
        <FilteredToNothing onClear={clearFilters} />
      ) : groupBy === "none" ? (
        <ArtifactsTable artifacts={data.artifacts} />
      ) : (
        <div className="space-y-3">
          {groupRows(data.artifacts, GROUP_DIMENSIONS[groupBy]).map(({ key, artifacts }) => (
            <ArtifactGroupSection
              key={key ?? "__absent__"}
              label={groupLabel(key, GROUP_DIMENSIONS[groupBy])}
              total={groupTotal(data.group_counts, key)}
              shown={artifacts.length}
              linkTo={groupLink(key, GROUP_DIMENSIONS[groupBy])}
            >
              <ArtifactsTable artifacts={artifacts} />
            </ArtifactGroupSection>
          ))}
        </div>
      )}

      <Pagination page={data} onPage={(next) => setParam("page", String(next))} />
    </div>
  );
}

/** Which rows of the page fall under each group key, in the page's order. */
function groupRows(
  artifacts: Artifact[],
  groupBy: GroupDimension
): { key: string | null; artifacts: Artifact[] }[] {
  const buckets = new Map<string | null, Artifact[]>();
  for (const artifact of artifacts) {
    const key = artifact[groupBy];
    const bucket = buckets.get(key);
    if (bucket) bucket.push(artifact);
    else buckets.set(key, [artifact]);
  }
  return [...buckets.entries()].map(([key, rows]) => ({ key, artifacts: rows }));
}

/**
 * The catalog-wide total for one group, or null when the server did not send
 * one.
 *
 * Null rather than the row count on purpose: the row count is the number on
 * this page, and presenting it where a total belongs is the exact substitution
 * this page exists to stop making.
 */
function groupTotal(counts: ArtifactFacetValue[] | null, key: string | null): number | null {
  const entry = (counts ?? []).find((c) => c.value === key);
  return entry ? entry.count : null;
}

/**
 * What this page is showing, in numbers that mean what they say.
 *
 * "251–450 of 1 340 match" — the range is the window, the total is the answer.
 * Neither is hedged, because neither is inferred from the rows in hand.
 */
function PageSummary({ page, filtered }: { page: ArtifactPage; filtered: boolean }): JSX.Element {
  const noun = page.total === 1 ? "artifact" : "artifacts";
  // The empty case is spelled out by the card below, which also offers the way
  // out of it; the summary line only says how many, so here it says none.
  if (page.total === 0) return <>Nothing matched.</>;
  const first = page.offset + 1;
  const last = page.offset + page.artifacts.length;
  const range = page.total > page.artifacts.length ? `${first}–${last} of ` : "";
  return (
    <>
      Showing {range}
      {page.total} {noun}
      {filtered ? " matching your filters." : " in the catalog."}
    </>
  );
}

function Pagination({
  page,
  onPage,
}: {
  page: ArtifactPage;
  onPage: (page: number) => void;
}): JSX.Element | null {
  const pageCount = Math.max(1, Math.ceil(page.total / page.limit));
  if (pageCount <= 1) return null;
  const current = Math.floor(page.offset / page.limit) + 1;
  return (
    <nav className="flex items-center gap-2" aria-label="Artifact pages">
      <Button
        variant="outline"
        size="sm"
        disabled={current <= 1}
        onClick={() => onPage(current - 1)}
      >
        Previous
      </Button>
      <span className="text-xs text-muted-foreground">
        Page {current} of {pageCount}
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={current >= pageCount}
        onClick={() => onPage(current + 1)}
      >
        Next
      </Button>
    </nav>
  );
}

/**
 * The four ways this read can fail, told apart (flowstack#923).
 *
 * Reuses the shared classifier rather than a fourth private copy; the copy is
 * this page's own, because "you cannot see these artifacts" and "this run could
 * not be found" are different sentences about different things.
 */
function ArtifactsError({ error }: { error: unknown }): JSX.Element {
  const kind = classifyApiError(error);
  const copy: Record<ReturnType<typeof classifyApiError>, { title: string; body: string }> = {
    network: {
      title: "Could not reach the server",
      body: "The request never got a response. Check your connection, then reload.",
    },
    forbidden: {
      title: "You cannot view this catalog",
      body: "Your session is not entitled to read this tenant's artifacts.",
    },
    "not-found": {
      title: "Artifact catalog not found",
      body: "The server has no artifact catalog at this address. This usually means the API is older than this portal build.",
    },
    server: {
      title: "The server could not list artifacts",
      body: "The FlowStack server returned an error. This is not something you can fix from here — retry, and report it if it persists.",
    },
    unknown: {
      title: "Could not load artifacts",
      body: apiErrorMessage(error),
    },
  };
  const { title, body } = copy[kind];
  return (
    <Card elevation="raised">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{body}</CardDescription>
      </CardHeader>
    </Card>
  );
}

function FilteredToNothing({ onClear }: { onClear: () => void }): JSX.Element {
  return (
    <Card elevation="raised">
      <CardHeader>
        <CardTitle>No artifacts match these filters</CardTitle>
        <CardDescription>
          There are artifacts in the catalog — none of them match what you asked for. The search
          covered the whole catalog, so widening the filters is the way to find them.
        </CardDescription>
        <div className="pt-2">
          <Button variant="outline" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        </div>
      </CardHeader>
    </Card>
  );
}

interface ArtifactToolbarProps {
  page: ArtifactPage;
  filters: ArtifactFilters;
  groupBy: GroupBy;
  onFilterChange: (name: string, value: string) => void;
  onClear: () => void;
}

/**
 * Search + filter selects + the group-by mode.
 *
 * The control idiom is `RunListAgGrid`'s, not a new one: an `Input` plus the
 * `ui/select` primitive at the same height, with the state in URL params.
 *
 * **Every option is a facet count over the catalog**, not a value scraped off
 * the loaded rows. That is the difference #963 makes to this toolbar: a type
 * that exists only outside the current page still has an option, and the
 * number beside it is how many artifacts carry it. Each dimension's counts are
 * computed with its own filter excluded, so a control can always be widened
 * again after it has been narrowed.
 *
 * A select is rendered whenever it has options at all. There is no Project
 * control: every artifact carries the same `PILOT_PROJECT_SLUG` placeholder
 * (flowstack#515 — the route says in as many words that consumers must not
 * filter on it while the deployment is single-project), so it can not tell any
 * two rows apart. `status` always offers the whole closed vocabulary,
 * including values with a count of zero, because its options are a fixed enum
 * rather than something read off the data (Rupert PR-259 r1).
 */
function ArtifactToolbar({
  page,
  filters,
  groupBy,
  onFilterChange,
  onClear,
}: ArtifactToolbarProps): JSX.Element {
  const types = optionsFromFacet(page.facets?.typed_object, "", "");
  const runs = runOptions(page.facets?.run_id);
  const tiles = tileOptions(page.facets?.producer_tile);
  const statuses = statusOptions(page.facets?.scientific_status);
  const anyFilter = hasActiveFilters(filters);

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label="Search artifacts"
          placeholder="Search by hash, type, tile, run…"
          value={filters.q}
          onChange={(e) => onFilterChange("q", e.target.value)}
          className="max-w-xs"
        />
        {types.length > 0 && (
          <FilterSelect
            label={FILTER_LABELS.typedObject}
            value={filters.typedObject}
            anyLabel="All types"
            options={types}
            onChange={(v) => onFilterChange("type", v)}
          />
        )}
        {runs.length > 0 && (
          <FilterSelect
            label={FILTER_LABELS.runId}
            value={filters.runId}
            anyLabel="All runs"
            options={runs}
            onChange={(v) => onFilterChange("run", v)}
          />
        )}
        {tiles.length > 0 && (
          <FilterSelect
            label={FILTER_LABELS.producerTile}
            value={filters.producerTile}
            anyLabel="All tiles"
            options={tiles}
            onChange={(v) => onFilterChange("tile", v)}
          />
        )}
        <FilterSelect
          label={FILTER_LABELS.scientificStatus}
          value={filters.scientificStatus}
          anyLabel="Any status"
          options={statuses}
          onChange={(v) => onFilterChange("status", v)}
        />
        <FilterSelect
          label="Group by"
          value={groupBy === "none" ? "" : groupBy}
          anyLabel="No grouping (all)"
          options={(Object.keys(GROUP_BY_LABELS) as Exclude<GroupBy, "none">[]).map((mode) => ({
            value: mode,
            label: GROUP_BY_LABELS[mode],
            count: 0,
          }))}
          showCounts={false}
          onChange={(v) => onFilterChange("group", v)}
        />
        {anyFilter && (
          <Button variant="outline" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        )}
      </div>
      {runs.length > 0 && (
        <p className="text-xs text-muted-foreground">
          The run list offers your most recent runs. Any run is still filterable by opening it from
          the Runs page — the list is a shortlist, not the limit of what can be asked for.
        </p>
      )}
    </div>
  );
}

interface FilterSelectProps {
  label: string;
  value: string;
  anyLabel: string;
  options: FilterOption[];
  showCounts?: boolean;
  onChange: (value: string) => void;
}

/** The `ui/select` primitive at toolbar height, with the "any" option first. */
function FilterSelect({
  label,
  value,
  anyLabel,
  options,
  showCounts = true,
  onChange,
}: FilterSelectProps): JSX.Element {
  return (
    <Select size="sm" aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{anyLabel}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {showCounts ? `${option.label} (${option.count})` : option.label}
        </option>
      ))}
    </Select>
  );
}

interface ArtifactGroupSectionProps {
  label: string;
  /** Catalog-wide count for this group, or null if the server sent none. */
  total: number | null;
  /** How many of that group's artifacts are on this page. */
  shown: number;
  linkTo: string | null;
  children: React.ReactNode;
}

/**
 * One collapsible group.
 *
 * The count is the group's TRUE size, taken from the server's grouped
 * aggregate over the filtered catalog — not from the rows in the section. When
 * the page holds fewer than that, both numbers are shown ("42 artifacts · 12
 * on this page"), because the alternative is a heading that contradicts what
 * is under it. #949 forbade the reverse substitution — "42 artifacts" must
 * mean 42 exist, not 42 were loaded — and the page-scoped hedge #964 replaced
 * it with is no longer needed now that the real number is available.
 */
function ArtifactGroupSection({
  label,
  total,
  shown,
  linkTo,
  children,
}: ArtifactGroupSectionProps): JSX.Element {
  const [open, setOpen] = React.useState(true);
  return (
    <section className="rounded-md border">
      <div className="flex items-center gap-2 border-b bg-muted/40 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          className="flex items-center gap-1 text-sm font-medium hover:underline"
        >
          {open ? (
            <ChevronDown className="h-4 w-4" aria-hidden />
          ) : (
            <ChevronRight className="h-4 w-4" aria-hidden />
          )}
          <span className="font-mono text-xs">{label}</span>
        </button>
        <span className="text-xs text-muted-foreground">
          {total === null ? (
            <>
              {shown} {shown === 1 ? "artifact" : "artifacts"} on this page
            </>
          ) : total > shown ? (
            <>
              {total} {total === 1 ? "artifact" : "artifacts"} · {shown} on this page
            </>
          ) : (
            <>
              {total} {total === 1 ? "artifact" : "artifacts"}
            </>
          )}
        </span>
        {linkTo && (
          <Link to={linkTo} className="ml-auto text-xs text-muted-foreground hover:underline">
            Open →
          </Link>
        )}
      </div>
      {open && children}
    </section>
  );
}

function ArtifactsTable({ artifacts }: { artifacts: Artifact[] }) {
  return (
    <div className="overflow-hidden rounded-md border">
      <Table>
        <TableHeader surface="tinted">
          <TableRow>
            <TableHead>Type</TableHead>
            <TableHead>Content hash</TableHead>
            <TableHead>Producer</TableHead>
            <TableHead>Project</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Size</TableHead>
            <TableHead>Created</TableHead>
            <TableHead align="right">
              <span className="sr-only">Download</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {artifacts.map((a) => (
            <TableRow key={a.artifact_id}>
              <TableCell>
                <Link
                  to={`/artifacts/${encodeURIComponent(a.artifact_id)}`}
                  className="hover:underline"
                >
                  <span className="font-mono text-xs">{a.typed_object}</span>
                  <span className="ml-1 text-xs text-muted-foreground">v{a.schema_version}</span>
                </Link>
              </TableCell>
              <TableCell className="font-mono text-xs">{truncateHash(a.content_hash)}</TableCell>
              <TableCell className="text-xs">
                {a.producer_tile ? (
                  a.run_id ? (
                    <Link to={`/runs/${a.run_id}`} className="hover:underline">
                      <span className="font-mono">{a.producer_tile}</span>
                    </Link>
                  ) : (
                    <span className="font-mono">{a.producer_tile}</span>
                  )
                ) : (
                  <span className="text-muted-foreground">user-supplied</span>
                )}
              </TableCell>
              <TableCell className="text-xs">
                <Link to={`/projects/${a.project_slug}`} className="hover:underline">
                  {a.project_slug}
                </Link>
              </TableCell>
              <TableCell className="text-xs">
                <ArtifactStatusBadge status={a.scientific_status} />
              </TableCell>
              <TableCell className="text-xs tabular-nums">{formatBytes(a.size_bytes)}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {formatTimestamp(a.created_at)}
              </TableCell>
              <TableCell align="right">
                <ArtifactDownloadButton contentHash={a.content_hash} compact />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function truncateHash(hash: string): string {
  // sha256:0123456789abcdef... -> sha256:01234567…
  if (hash.length <= 16) return hash;
  const [prefix, rest] = hash.includes(":") ? hash.split(":", 2) : ["", hash];
  const shown = (rest ?? "").slice(0, 8);
  return prefix ? `${prefix}:${shown}…` : `${shown}…`;
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
